import { signUp } from "@/api/signUp";
import EnfiniBoostLogo from "@/assets/images/boost-full-logo-color.png";
import EnfiniShieldtLogo from "@/assets/images/shield-full-logo-color.png";
import AdceFooter from "@/components/adcelerate/Footer";
import Footer from "@/components/enfini/Footer";
import ChannelLinks from "@/components/signup/ChannelLinks";
import OptIns from "@/components/signup/OptIns";
import PersonalInformation from "@/components/signup/PersonalInformation";
import Sign from "@/components/signup/Sign";
import ToastModal from "@/components/ToastModal";
import { boostContractContent } from "@/utils/contracts/boostContractContent";
import { generateContractPdf } from "@/utils/contracts/contractTemplate";
import { shieldContractContent } from "@/utils/contracts/shieldContractContent";
import {
  alphabetChecker,
  isValidEmail,
  isValidUrl,
} from "@/utils/inputChecker";
import axios from "axios";
import { useEffect, useRef, useState } from "react";

export default function Esign() {
  const landingPage = import.meta.env.VITE_LANDING_PAGE;

  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    country: "",
    socials: {
      youtube: "",
      tiktok: "",
      facebook: "",
      instagram: "",
      twitchTv: "",
      douyin: "",
      kuaishou: "",
      rumble: "",
      others: "",
    },
    optIn: {
      msnDistribution: true,
      youtube: false,
      facebook: false,
      instagram: false,
      bilibili: false,
      tiktok: false,
      snapchatStars: false,
    },
    signature: "",
    tos: false,
    contentId: false,
    profitGuarantee: false,
    informationAccurate: false,
    service_type: {
      shield: "enfinishield",
      boost: "enfiniboost",
    },
  });

  const handleUserDataInput = (e) => {
    if (typeof e === "string") {
      setUserData((prev) => ({
        ...prev,
        country: e,
      }));
      return;
    }

    if (typeof e === "boolean") {
      setUserData((prev) => ({ ...prev, tos: e, partnerProgram: e }));
      return;
    }

    const { id, value, type, checked } = e.target;

    const sanitizedValue =
      ["firstName", "lastName"].includes(id) && type === "text"
        ? alphabetChecker(value)
        : value;

    setUserData((prev) => ({
      ...prev,
      [id]: type === "checkbox" ? checked : sanitizedValue,
    }));
  };

  const handleSocialChange = (e) => {
    const { id, value } = e.target;
    setUserData((prev) => ({
      ...prev,
      socials: {
        ...prev.socials,
        [id]: value,
      },
    }));
  };

  const handleOptInChange = (platformKey, checked) => {
    setUserData((prev) => ({
      ...prev,
      optIn: {
        ...prev.optIn,
        [platformKey]: checked,
      },
    }));
  };

  const signatureRef = useRef(null);
  const clearSignature = () => {
    signatureRef.current?.clear();
  };

  const undoSignature = () => {
    const data = signatureRef.current?.toData();
    if (data && data.length) {
      data.pop();
      signatureRef.current?.fromData(data);
    }
  };

  const handleEndStroke = () => {
    if (signatureRef.current) {
      const signatureData = signatureRef.current.toDataURL();
      setUserData((prev) => ({ ...prev, signature: signatureData }));
    }
  };

  const [ipAddress, setIpAddress] = useState("");
  useEffect(() => {
    const fetchIp = async () => {
      try {
        const response = await axios.get("https://api.ipify.org?format=json");
        setIpAddress(response.data.ip);
      } catch (error) {
        console.error("Error fetching IP address:", error);
      }
    };

    fetchIp();
  }, []);

  const [errorMessage, setErrorMessage] = useState("");
  const validateForm = () => {
    const {
      firstName,
      lastName,
      email,
      country,
      socials,
      signature,
      tos,
      informationAccurate,
    } = userData;

    // Basic Info
    if (!firstName) {
      setErrorMessage("First Name is required.");
      return false;
    }

    if (!lastName) {
      setErrorMessage("Last Name is required.");
      return false;
    }

    if (!email) {
      setErrorMessage("Email is required.");
      return false;
    }

    if (!isValidEmail(email)) {
      setErrorMessage("Please enter a valid email address.");
      return false;
    }

    if (!country) {
      setErrorMessage("Country is required.");
      return false;
    }

    // Socials
    const hasAtLeastOneLink = Object.values(socials).some(
      (link) => link.trim() !== ""
    );

    if (!hasAtLeastOneLink) {
      setErrorMessage("Please provide at least one social media link.");
      return false;
    }

    const invalidLinks = Object.entries(socials)
      .filter(([_, link]) => link.trim() !== "")
      .filter(([_, link]) => !isValidUrl(link));

    if (invalidLinks.length > 0) {
      const [platform] = invalidLinks[0];
      setErrorMessage(`Please enter a valid URL for ${platform}`);
      return false;
    }

    // Final Agreement
    if (!signature) {
      setErrorMessage("Signature is required.");
      return false;
    }

    if (!tos) {
      setErrorMessage("You must accept the Terms of Service.");
      return false;
    }

    if (!informationAccurate) {
      setErrorMessage(
        "You must confirm your information is accurate to continue."
      );
      return false;
    }

    setErrorMessage("");
    return true;
  };

  const [successToast, setSuccessToast] = useState(false);
  const [toastType, setToastType] = useState(false);
  const [errorPrompt, setErrorPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSignUp = async () => {
    if (!validateForm()) return;

    if (!recaptchaToken) {
      setErrorPrompt("Please verify you're not a robot.");
      return;
    }

    const userDetails = {
      first_name: userData.firstName,
      last_name: userData.lastName,
      email: userData.email,
      country: userData.country,
      user_socials: {
        youtube: userData.socials.youtube ? userData.socials.youtube : "N/A",
        tiktok: userData.socials.tiktok ? userData.socials.tiktok : "N/A",
        twitch: userData.socials.twitchTv,
        instagram: userData.socials.instagram
          ? userData.socials.instagram
          : "N/A",
        facebook: userData.socials.facebook ? userData.socials.facebook : "N/A",
        douyin: userData.socials.douyin,
        kuaishou: userData.socials.kuaishou,
        rumble: userData.socials.rumble,
        others: userData.socials.others,
      },
      videos_protection_consent: userData.contentId,
      tos: userData.tos,
      information_accurate: userData.informationAccurate,
      profit_guarantee: userData.profitGuarantee,
      service_type: userData.service_type[landingPage],
      dashboard_name: "enfinipay",
      optIn: {
        "MSN Distribution": userData.optIn.msnDistribution,
        YouTube: userData.optIn.youtube,
        Facebook: userData.optIn.facebook,
        Instagram: userData.optIn.instagram,
        Bilibili: userData.optIn.bilibili,
        TikTok: userData.optIn.tiktok,
        "Snapchat Stars": userData.optIn.snapchatStars,
      },
    };

    const file =
      landingPage === "boost"
        ? await generateContractPdf(boostContractContent, {
            signatureData: {
              user: userDetails,
              ipAddress,
              signature: userData.signature,
            },
            assets: { logo: EnfiniBoostLogo },
            fileName: (ctx) =>
              `Enfinity_Limited_MSN_+_more_${ctx.user?.first_name}_${ctx.user?.last_name}_${ctx.user?.email}.pdf`,
          })
        : await generateContractPdf(shieldContractContent, {
            signatureData: {
              user: userDetails,
              ipAddress,
              signature: userData.signature,
            },
            assets: { logo: EnfiniShieldtLogo },
            fileName: (ctx) =>
              `Enfinity_Limited_MSN_+_more_${ctx.user?.first_name}_${ctx.user?.last_name}_${ctx.user?.email}.pdf`,
          });

    // Convert user_socials and opt_in to JSON strings for backen
    const formData = new FormData();
    formData.append("first_name", userDetails.first_name);
    formData.append("last_name", userDetails.last_name);
    formData.append("email", userDetails.email);
    formData.append("country", userDetails.country);
    formData.append("service_type", userDetails.service_type);
    formData.append("user_socials", JSON.stringify(userDetails.user_socials));
    formData.append("opt_in", JSON.stringify(userDetails.optIn));
    formData.append("tos", userDetails.tos);
    formData.append(
      "videos_protection_consent",
      userDetails.videos_protection_consent
    );
    formData.append("profit_guarantee", userDetails.profit_guarantee);
    formData.append("information_accurate", userDetails.information_accurate);
    formData.append("dashboard_name", userDetails.dashboard_name);
    formData.append("contract_file", file);
    formData.append("recaptcha_token", recaptchaToken);

    setIsLoading(true);

    try {
      const signUpResponse = await signUp(formData);

      if (signUpResponse.success) {
        setSuccessToast(true);
        setToastType("success");
        setRecaptchaToken("");
        setUserData({
          firstName: "",
          lastName: "",
          email: "",
          country: "",
          socials: {
            youtube: "",
            tiktok: "",
            facebook: "",
            instagram: "",
            twitchTv: "",
            douyin: "",
            kuaishou: "",
            rumble: "",
            others: "",
          },
          optIn: {
            msnDistribution: true,
            youtube: false,
            facebook: false,
            instagram: false,
            bilibili: false,
            tiktok: false,
            snapchatStars: false,
          },
          signature: "",
          tos: false,
          contentId: false,
          profitGuarantee: false,
          informationAccurate: false,
        });
      } else {
        setErrorPrompt(
          signUpResponse.message || "Something went wrong. Please try again."
        );
      }
    } catch (error) {
      console.error("Sign-up error:", error);
      setErrorPrompt(
        error.message || "Something went wrong. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const title = {
    boost: "Sign up and get boosted now",
    shield: "Sign up and get protected now",
    adcelerate: "Sign in with Adcelerate",
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <>
      <div
        className={`space-y-12 pt-28 lg:pt-40 ${landingPage === "adcelerate"
          ? "bg-[#F7F8FA]"
          : "bg-[linear-gradient(45deg,var(--primary),var(--tertiary))]"
          }`}
      >
        <div className="flex flex-col justify-center items-center">
          <h1
            className={`text-4xl md:text-5xl xl:text-6xl mb-6 text-center px-10 ${landingPage === "adcelerate"
              ? "adce-hero font-bold text-primary"
              : "text-white font-medium"
              }`}
          >
            {title[landingPage]}
          </h1>
          <div className="flex flex-col justify-center items-center w-80 md:w-[30rem] xl:w-[50rem] z-40 text-primaryLight">
            <PersonalInformation
              userData={userData}
              handleUserDataInput={handleUserDataInput}
            />
            <ChannelLinks
              userData={userData}
              handleSocialChange={handleSocialChange}
            />
            {(landingPage === "boost" || landingPage === "adcelerate") && (
              <OptIns
                userData={userData}
                handleOptInChange={handleOptInChange}
              />
            )}
            <Sign
              signatureRef={signatureRef}
              userData={userData}
              setUserData={setUserData}
              clearSignature={clearSignature}
              undoSignature={undoSignature}
              handleEndStroke={handleEndStroke}
              isLoading={isLoading}
              handleSignUp={handleSignUp}
              errorMessage={errorMessage}
              setRecaptchaToken={setRecaptchaToken}
              errorPrompt={errorPrompt}
            />
          </div>
        </div>
        {landingPage === "adcelerate" ? <AdceFooter /> : <Footer />}
      </div>

      <ToastModal
        showToast={successToast}
        setShowToast={setSuccessToast}
        form="esign"
        type={toastType}
      />
    </>
  );
}
